import { useState, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Button, CircularProgress, TextField, FormControl,
    FormLabel, RadioGroup, FormControlLabel, Radio
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { getCurrentMonthRange, isValidDateRange, getCurrentDate } from '../../../utils/dateUtils';
import '../Subcontract/Index.css';

const CompletedJobsReport = () => {
    const { token, companyId, isAuthenticated, logout } = useAuth();
    
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Filter states
    const [reportType, setReportType] = useState('stamping'); // stamping or amc
    const [statusFilter, setStatusFilter] = useState('completed'); // pending, completed, upcoming
    
    // Initialize with current month range
    const defaultRange = getCurrentMonthRange();
    const [fromDate, setFromDate] = useState(defaultRange.fromDate);
    const [toDate, setToDate] = useState(defaultRange.toDate);
    const [dateError, setDateError] = useState('');
    
    const [selectedSubcontractor, setSelectedSubcontractor] = useState('');
    const [subcontractors, setSubcontractors] = useState([]);

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        fetchSubcontractors();
        setFromDate(getFormattedStartDate());
        setToDate(getFormattedCurrentDate());
        // Token is not expired, fetch data
        fetchTableData();
    }, [isAuthenticated, token]);

    // Validate and correct dates on component mount
    useEffect(() => {
        const currentFromDate = fromDate;
        const currentToDate = toDate;
        
        if (!isValidDateRange(currentFromDate, currentToDate)) {
            console.log('Invalid date range detected, correcting...', { currentFromDate, currentToDate });
            const correctedRange = getCurrentMonthRange();
            setFromDate(correctedRange.fromDate);
            setToDate(correctedRange.toDate);
            setDateError('');
        }
    }, []);

    // Validate dates whenever they change
    useEffect(() => {
        if (fromDate && toDate && !isValidDateRange(fromDate, toDate)) {
            setDateError('Invalid date range: From date cannot be after To date');
        } else if (dateError && fromDate && toDate && isValidDateRange(fromDate, toDate)) {
            setDateError('');
        }
    }, [fromDate, toDate]);

    // Date validation functions
    const handleFromDateChange = (newFromDate) => {
        setDateError('');
        
        if (!isValidDateRange(newFromDate, toDate)) {
            setDateError('From date cannot be after To date');
            return;
        }
        
        if (new Date(newFromDate) > new Date(getCurrentDate())) {
            setDateError('From date cannot be in the future');
            return;
        }
        
        setFromDate(newFromDate);
    };

    const handleToDateChange = (newToDate) => {
        setDateError('');
        
        if (!isValidDateRange(fromDate, newToDate)) {
            setDateError('To date cannot be before From date');
            return;
        }
        
        if (new Date(newToDate) > new Date(getCurrentDate())) {
            setDateError('To date cannot be in the future');
            return;
        }
        
        setToDate(newToDate);
    };

    // Function to fix invalid date ranges
    const fixDateRange = () => {
        const correctedRange = getCurrentMonthRange();
        setFromDate(correctedRange.fromDate);
        setToDate(correctedRange.toDate);
        setDateError('');
    };

    const getFormattedCurrentDate = () => {
        const currentDate = new Date();
        return currentDate.toISOString().split('T')[0];
    };

    const getFormattedStartDate = () => {
        const monthStartDate = new Date();
        monthStartDate.setDate(1);
        return monthStartDate.toISOString().split('T')[0];
    };

    const fetchSubcontractors = async () => {
        try {
            const response = await fetchData('SubContractAPI', `/${companyId}`, token);
            if (response.responseCode === '1111') {
                setSubcontractors(response.response_data);
            }
        } catch (error) {
            console.error('Error fetching subcontractors:', error);
        }
    };

    const fetchTableData = async () => {
        if (!fromDate || !toDate) {
            alert('Please select both From Date and To Date');
            return;
        }

        setLoading(true);
        try {
            // istype: 1 for stamping, 2 for amc
            const istype = reportType === 'stamping' ? 1 : 2;
            const subcontractorId = selectedSubcontractor || 0;
            
            const response = await fetchData(
                'ReportAPI',
                `/CompletedJobs/${companyId}/${fromDate}/${toDate}/${istype}/${subcontractorId}`,
                token
            );
            
            console.log('Completed Jobs Response:', response);
            
            if (response.responseCode === '1111' && response.response_data) {
                setData(response.response_data);
            } else {
                setData([]);
            }
        } catch (error) {
            console.error('Error fetching completed jobs:', error);
            setData([]);
        }
        setLoading(false);
    };

    const handleRefresh = () => {
        fetchTableData();
    };

    return (
        <div className="subcontract-index-wrapper">
            {/* Header */}
            <div className="subcontract-index-header">
                <div className="header-left">
                    <h1 className="index-title">Completed Jobs Report</h1>
                    <p className="index-subtitle">View and filter completed job reports</p>
                </div>
            </div>

            {/* Filters Section */}
            <div className="filters-section" style={{ 
                background: '#fff', 
                padding: '20px', 
                borderRadius: '12px', 
                marginBottom: '20px',
                position: 'relative',
                zIndex: 1,
                isolation: 'isolate'
            }}>
                {/* Radio Buttons for Report Type */}
                <FormControl component="fieldset" style={{ marginBottom: '20px' }}>
                    <FormLabel component="legend" style={{ fontSize: '13px', fontWeight: '600', color: '#475569' }}>
                        Report Type
                    </FormLabel>
                    <RadioGroup
                        row
                        value={reportType}
                        onChange={(e) => setReportType(e.target.value)}
                    >
                        <FormControlLabel
                            value="stamping"
                            control={<Radio size="small" />}
                            label="Stamping"
                            style={{ fontSize: '13px' }}
                        />
                        <FormControlLabel
                            value="amc"
                            control={<Radio size="small" />}
                            label="AMC"
                            style={{ fontSize: '13px' }}
                        />
                    </RadioGroup>
                </FormControl>

                {/* Filter Fields - All in one row */}
                <div style={{ 
                    display: 'flex', 
                    gap: '16px', 
                    alignItems: 'flex-end', 
                    flexWrap: 'wrap',
                    position: 'relative',
                    zIndex: 1
                }}>
                    {dateError && (
                        <div style={{ 
                            width: '100%',
                            color: '#dc2626', 
                            fontSize: '14px', 
                            marginBottom: '8px',
                            padding: '8px 12px',
                            backgroundColor: '#fef2f2',
                            border: '1px solid #fecaca',
                            borderRadius: '6px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <span>{dateError}</span>
                            <Button
                                size="small"
                                onClick={fixDateRange}
                                style={{
                                    marginLeft: '12px',
                                    fontSize: '12px',
                                    padding: '4px 8px',
                                    backgroundColor: '#dc2626',
                                    color: 'white',
                                    textTransform: 'none'
                                }}
                            >
                                Fix Dates
                            </Button>
                        </div>
                    )}
                    <TextField
                        label="From Date"
                        type="date"
                        value={fromDate}
                        onChange={(e) => handleFromDateChange(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        style={{ minWidth: '180px', flex: '1' }}
                        inputProps={{ max: getCurrentDate() }}
                        error={!!dateError}
                    />

                    <TextField
                        label="To Date"
                        type="date"
                        value={toDate}
                        onChange={(e) => handleToDateChange(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        style={{ minWidth: '180px', flex: '1' }}
                        inputProps={{ 
                            min: fromDate,
                            max: getCurrentDate() 
                        }}
                        error={!!dateError}
                    />

                    <div style={{ minWidth: '150px', flex: '1' }}>
                        <label style={{ 
                            display: 'block', 
                            fontSize: '12px', 
                            color: 'rgba(0, 0, 0, 0.6)', 
                            marginBottom: '8px',
                            fontWeight: 400,
                            transform: 'translate(14px, -9px) scale(0.75)',
                            transformOrigin: 'top left',
                            position: 'absolute',
                            backgroundColor: 'white',
                            padding: '0 4px',
                            zIndex: 1
                        }}>Status</label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            style={{
                                width: '100%',
                                height: '40px',
                                padding: '8.5px 14px',
                                border: '1px solid rgba(0, 0, 0, 0.23)',
                                borderRadius: '4px',
                                fontSize: '14px',
                                backgroundColor: 'white',
                                cursor: 'pointer',
                                outline: 'none',
                                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                                position: 'relative'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#1976d2'}
                            onBlur={(e) => e.target.style.borderColor = 'rgba(0, 0, 0, 0.23)'}
                        >
                            <option value="pending">Pending</option>
                            <option value="completed">Completed</option>
                            <option value="upcoming">Upcoming</option>
                        </select>
                    </div>

                    <div style={{ minWidth: '200px', flex: '1', position: 'relative' }}>
                        <label style={{ 
                            display: 'block', 
                            fontSize: '12px', 
                            color: 'rgba(0, 0, 0, 0.6)', 
                            marginBottom: '8px',
                            fontWeight: 400,
                            transform: 'translate(14px, -9px) scale(0.75)',
                            transformOrigin: 'top left',
                            position: 'absolute',
                            backgroundColor: 'white',
                            padding: '0 4px',
                            zIndex: 1
                        }}>Subcontractor</label>
                        <select
                            value={selectedSubcontractor}
                            onChange={(e) => setSelectedSubcontractor(e.target.value)}
                            style={{
                                width: '100%',
                                height: '40px',
                                padding: '8.5px 14px',
                                border: '1px solid rgba(0, 0, 0, 0.23)',
                                borderRadius: '4px',
                                fontSize: '14px',
                                backgroundColor: 'white',
                                cursor: 'pointer',
                                outline: 'none',
                                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                                position: 'relative'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#1976d2'}
                            onBlur={(e) => e.target.style.borderColor = 'rgba(0, 0, 0, 0.23)'}
                        >
                            <option value="">All Subcontractors</option>
                            {subcontractors.map((sub) => (
                                <option key={sub.Id} value={sub.Id}>
                                    {sub.Name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Search Button */}
                    <Button
                        variant="contained"
                        onClick={fetchTableData}
                        startIcon={<SearchIcon />}
                        disabled={loading}
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: '#fff',
                            textTransform: 'none',
                            fontWeight: '600',
                            minWidth: '120px',
                            height: '40px'
                        }}
                    >
                        {loading ? 'Searching...' : 'Search'}
                    </Button>

                    {/* Refresh Button */}
                    <Button
                        variant="outlined"
                        onClick={handleRefresh}
                        startIcon={<RefreshIcon />}
                        disabled={loading}
                        style={{
                            borderColor: '#667eea',
                            color: '#667eea',
                            textTransform: 'none',
                            fontWeight: '600',
                            minWidth: '120px',
                            height: '40px'
                        }}
                    >
                        Refresh
                    </Button>
                </div>
            </div>

            {/* Table */}
            <div className="table-container">
                {loading ? (
                    <div className="loading-container">
                        <CircularProgress />
                        <p>Loading report...</p>
                    </div>
                ) : data.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-icon">📊</div>
                        <h3>No Data Found</h3>
                        <p>Select filters and click Search to view the report</p>
                    </div>
                ) : (
                    <TableContainer component={Paper} className="modern-table">
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell className="table-header">Sl No</TableCell>
                                    <TableCell className="table-header">Order Date</TableCell>
                                    <TableCell className="table-header">Order Number</TableCell>
                                    <TableCell className="table-header">Customer Name</TableCell>
                                    <TableCell className="table-header">State</TableCell>
                                    <TableCell className="table-header">Status</TableCell>
                                    <TableCell className="table-header">Completed Date</TableCell>
                                    <TableCell className="table-header">Subcontractor</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.map((item, index) => (
                                    <TableRow key={index} className="table-row">
                                        <TableCell className="table-cell">
                                            <span className="serial-number">{index + 1}</span>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            {item.OrderDate ? new Date(item.OrderDate).toLocaleDateString() : '-'}
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <span className="subcontract-name">{item.OrderNumber}</span>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            {item.CustomerName}
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            {item.StateName || '-'}
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            <span style={{
                                                padding: '4px 8px',
                                                borderRadius: '4px',
                                                fontSize: '11px',
                                                fontWeight: '600',
                                                background: item.StatusName === 'Completed' ? '#d1fae5' :
                                                    item.StatusName === 'Pending' ? '#fef3c7' : '#dbeafe',
                                                color: item.StatusName === 'Completed' ? '#065f46' :
                                                    item.StatusName === 'Pending' ? '#92400e' : '#1e40af'
                                            }}>
                                                {item.StatusName}
                                            </span>
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            {item.CompletedDate ? new Date(item.CompletedDate).toLocaleDateString() : '-'}
                                        </TableCell>
                                        <TableCell className="table-cell">
                                            {item.Subcontractname || '-'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </div>

            {/* Summary */}
            {!loading && data.length > 0 && (
                <div className="summary-section">
                    <span className="summary-text">
                        Showing {data.length} completed jobs
                    </span>
                </div>
            )}
        </div>
    );
};

export default CompletedJobsReport;
